import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    ResponsiveDialog,
    RichTextArea,
    Select,
    Stack,
    styled,
    Text,
    Tooltip,
} from 'shell/components';
import {
    CUSTOM_CONTENT_WORD_COUNT_LIMIT,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { getHTMLWordCount, getTooltipComponent } from 'utils/helper';

const getLanguageLevelText = (languageLevel, t) => {
    return {
        'middle-school': t(
            'feedback-settings.language-level.middle-school-description',
            'Middle School: Plain and straightforward feedback. Ideal for middle school students or those with basic knowledge.'
        ),
        'high-school': t(
            'feedback-settings.language-level.high-school-description',
            'High School: Moderately complex feedback. Suited for high school students or those with intermediate understanding.'
        ),
        undergraduate: t(
            'feedback-settings.language-level.undergraduate-description',
            'Undergraduate (Default): Complex feedback requiring higher-level understanding. Geared towards college-level learners or those with advanced knowledge.'
        ),
        graduate: t(
            'feedback-settings.language-level.graduate-description',
            'Graduate +: Very intricate feedback demanding advanced comprehension. Targeted at graduate students or individuals with expert-level understanding.'
        ),
    }[languageLevel];
};

const getToneText = (tone, t) => {
    return {
        'very-supportive': t(
            'feedback-settings.tone.very-supportive-description',
            'Very Supportive: Feedback is overwhelmingly positive and focuses primarily on strengths, offering minimal critique or suggestions for improvement.'
        ),
        supportive: t(
            'feedback-settings.tone.supportive-description',
            'Supportive: Feedback is generally positive, highlighting strengths while gently pointing out areas for improvement in a supportive manner.'
        ),
        balanced: t(
            'feedback-settings.tone.balanced-description',
            'Balanced (Default): Feedback strikes a balance between positive reinforcement and constructive criticism, providing both praise and suggestions for improvement in equal measure.'
        ),
        critical: t(
            'feedback-settings.tone.critical-description',
            'Critical:  Feedback is straightforward and analytical, pinpointing mistakes and areas needing improvement directly. It emphasizes the importance of addressing these areas to facilitate growth and development.'
        ),
    }[tone];
};

const InlineCommentsSettingsDialog = memo(({ open, form: data, isInlineCommentsSettingsDefault, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(null);

    const handleClose = () => {
        if (form.isDirty) {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit now, any changes you have made to feedback settings will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    onClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            onClose();
        }
    };

    const handleChange = (changes) => {
        if (typeof changes === 'function') {
            setForm((current) => {
                const newForm = changes(current);
                return {
                    ...newForm,
                    isDirty: true,
                };
            });
        } else {
            setForm((current) => ({
                ...current,
                ...changes,
                isDirty: true,
            }));
        }
    };

    const handleClickReset = () => {
        handleChange({
            maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
            commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
            languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
            tone: DEFAULT_INLINE_COMMENTS_TONE,
        });
    };

    const validateAll = () => {
        if (getHTMLWordCount(form.inlineCommentsCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT) {
            message.error(
                t('error.additional-instructions-too-long', 'Additional instructions exceeds {{wordCount}} words.', {
                    wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT,
                })
            );
            return false;
        }
        return true;
    };

    const handleClickSave = () => {
        const isValid = validateAll();
        if (!isValid) return;
        onSave(omit(form, ['isDirty']));
        onClose();
    };

    useEffect(() => {
        if (open) {
            setForm({
                ...data.inlineCommentsSettings,
                inlineCommentsCustomContext: data.inlineCommentsCustomContext,
                isDirty: false,
            });
        }
    }, [data.inlineCommentsCustomContext, data.inlineCommentsSettings, open]);

    if (!form) return null;

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('inline-comments-section.settings.title', 'Advanced Inline Comments Settings')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={5}>
                    <Stack sx={{ maxWidth: '800px' }} spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p1'}>{t('common:ui.settings', 'Settings')}</Text>
                            <Button
                                color={'neutral'}
                                disabled={isInlineCommentsSettingsDefault(form)}
                                variant={'borderless'}
                                onClick={handleClickReset}
                            >
                                {t('reset-to-system-default', 'Reset to System Default')}
                            </Button>
                        </Stack>
                        <Paper variant={'outlined'}>
                            <Stack sx={device !== 'mobile' ? { position: 'relative' } : undefined} spacing={7}>
                                <CustomSelect
                                    label={
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Text variant={'p1'} weight={'semibold'}>
                                                {t(
                                                    'inline-comments-section.settings.max-comments',
                                                    'Maximum Comments per Page'
                                                )}
                                            </Text>
                                            <Tooltip
                                                content={getTooltipComponent(
                                                    t(
                                                        'inline-comments-section.settings.max-comments',
                                                        'Maximum Comments per Page'
                                                    ),
                                                    t(
                                                        'inline-comments-section.settings.max-comments-tooltip',
                                                        'Controls the maximum number of unique comments TimelyGrader will generate per page for you to review or edit.'
                                                    )
                                                )}
                                                type={'info'}
                                            />
                                        </Stack>
                                    }
                                    labelId={'max-comments-per-page-select'}
                                    value={form.maxCommentsPerPage}
                                    onChange={(e) => handleChange({ maxCommentsPerPage: e.target.value })}
                                >
                                    <MenuItem value={'1'}>1</MenuItem>
                                    <MenuItem value={'2'}>2 ({t('common:ui.default', 'Default')})</MenuItem>
                                    <MenuItem value={'3'}>3</MenuItem>
                                </CustomSelect>
                                <CustomSelect
                                    label={
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Text variant={'p1'} weight={'semibold'}>
                                                {t(
                                                    'inline-comments-section.settings.comment-length',
                                                    'Length of Each Comment'
                                                )}
                                            </Text>
                                            <Tooltip
                                                content={getTooltipComponent(
                                                    t(
                                                        'inline-comments-section.settings.comment-length',
                                                        'Length of Each Comment'
                                                    ),
                                                    t(
                                                        'inline-comments-section.settings.comment-length-tooltip',
                                                        'Sets the length of each individual comment generated. For simpler assignments, shorter comments can help provide guidance without overwhelming students.'
                                                    )
                                                )}
                                                type={'info'}
                                            />
                                        </Stack>
                                    }
                                    labelId={'comment-length-select'}
                                    value={form.commentLength}
                                    onChange={(e) => handleChange({ commentLength: e.target.value })}
                                >
                                    <MenuItem value={'<25'}>
                                        {t('common:word-length.under-word-count', 'Under {{wordCount}} words', {
                                            wordCount: 25,
                                        })}
                                    </MenuItem>
                                    <MenuItem value={'25-50'}>
                                        {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                            wordCountRange: '25-50',
                                        })}
                                    </MenuItem>
                                    <MenuItem value={'50-75'}>
                                        {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                            wordCountRange: '50-75',
                                        })}
                                    </MenuItem>
                                </CustomSelect>
                                <Stack spacing={3}>
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            {t('feedback-settings.language-level.title', 'Language Level')}
                                        </Text>
                                        <Tooltip
                                            content={getTooltipComponent(
                                                t('feedback-settings.language-level.title', 'Language Level'),
                                                t(
                                                    'feedback-settings.language-level.feedback-tooltip',
                                                    'Defines the language proficiency at which comments are communicated. Each level is assessed based on the Flesch-Kincaid Grade Level Scale.'
                                                )
                                            )}
                                            type={'info'}
                                        />
                                    </Stack>
                                    <CustomRadioGroup
                                        aria-label={'language level'}
                                        row={device !== 'mobile'}
                                        value={form.languageLevel}
                                        onChange={(e) => handleChange({ languageLevel: e.target.value })}
                                    >
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t(
                                                    'feedback-settings.language-level.middle-school-label',
                                                    'Middle School'
                                                )}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'middle-school'}
                                            />
                                        </CustomStack>
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t(
                                                    'feedback-settings.language-level.high-school-label',
                                                    'High School'
                                                )}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'high-school'}
                                            />
                                        </CustomStack>
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t(
                                                    'feedback-settings.language-level.undergraduate-label',
                                                    'Undergraduate'
                                                )}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'undergraduate'}
                                            />
                                        </CustomStack>
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t(
                                                    'feedback-settings.language-level.graduate-label',
                                                    'Graduate +'
                                                )}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'graduate'}
                                            />
                                        </CustomStack>
                                    </CustomRadioGroup>
                                    <Box
                                        sx={{
                                            ...(device !== 'mobile' && {
                                                height: '32px',
                                            }),
                                        }}
                                    >
                                        <Text>{getLanguageLevelText(form.languageLevel, t)}</Text>
                                    </Box>
                                </Stack>
                                <Stack spacing={3}>
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            {t('feedback-settings.tone.comment-title', 'Commentary Tone')}
                                        </Text>
                                        <Tooltip
                                            content={getTooltipComponent(
                                                t('feedback-settings.tone.comment-title', 'Commentary Tone'),
                                                t(
                                                    'feedback-settings.tone.comment-tooltip',
                                                    "Describes how comments are articulated, and whether they lean towards being supportive or critical, depending on the assignment's demands and the student audience involved."
                                                )
                                            )}
                                            type={'info'}
                                        />
                                    </Stack>
                                    <CustomRadioGroup
                                        aria-label={'inline comments tone'}
                                        row={device !== 'mobile'}
                                        value={form.tone}
                                        onChange={(e) => handleChange({ tone: e.target.value })}
                                    >
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t(
                                                    'feedback-settings.tone.very-supportive-label',
                                                    'Very Supportive'
                                                )}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'very-supportive'}
                                            />
                                        </CustomStack>
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t('feedback-settings.tone.supportive-label', 'Supportive')}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'supportive'}
                                            />
                                        </CustomStack>
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t('feedback-settings.tone.balanced-label', 'Balanced')}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'balanced'}
                                            />
                                        </CustomStack>
                                        <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                            <Radio
                                                label={t('feedback-settings.tone.critical-label', 'Critical')}
                                                labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                                value={'critical'}
                                            />
                                        </CustomStack>
                                    </CustomRadioGroup>
                                    <Text>{getToneText(form.tone, t)}</Text>
                                </Stack>
                                <RichTextArea
                                    error={
                                        getHTMLWordCount(form.inlineCommentsCustomContext) >
                                        CUSTOM_CONTENT_WORD_COUNT_LIMIT
                                    }
                                    helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                        wordLimitStatus: `${getHTMLWordCount(
                                            form.inlineCommentsCustomContext
                                        )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                    })}
                                    helperTextPosition={'end'}
                                    label={t(
                                        'feedback-settings.additional-instruction.label',
                                        'Additional Instructions for the AI'
                                    )}
                                    placeholder={t(
                                        'feedback-settings.additional-instruction.comment-placeholder',
                                        'Do you have any additional instructions about the content of comments?'
                                    )}
                                    value={form.inlineCommentsCustomContext}
                                    onChange={(value) => handleChange({ inlineCommentsCustomContext: value })}
                                />
                            </Stack>
                        </Paper>
                    </Stack>
                </Stack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} spacing={4}>
                    <Button aria-hidden={false} color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleClickSave}>{t('common:controls.save-settings', 'Save Settings')}</Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiInputBase-root': {
            width: '150px',
        },
    };
});

const CustomRadioGroup = styled(RadioGroup)(() => {
    return {
        justifyContent: 'space-between',
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        width: '150px',
        '& > .MuiFormControlLabel-root': {
            width: 'fit-content',
        },
    };
});

export default InlineCommentsSettingsDialog;
